import { useMutation, useLazyQuery } from "@apollo/client";
import {
  FC,
  ReactElement,
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { UPDATE_USER } from "../data/mutation";
import { GET_USER } from "src/hooks/data/queries";
import { saveUserState, getUserState, clearAllState } from "src/helpers/indexedDBHelper";


const UserContext = createContext<{
  user: any; //change later,
  setUser: (payload: any) => void;
  logoutUser: () => void;
  saveUser: (payload: any, callBack?: any) => void;
  userRefetch?: any;
}>({
  user: null,
  saveUser: ({}, callback) => {},
  logoutUser: () => {},
  setUser: ({}) => {},
  userRefetch: () => {},
});
export const useUser = () => useContext(UserContext);

export const UserProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [getUser, { data, refetch }] = useLazyQuery(GET_USER, {
    fetchPolicy: "no-cache",
  });
  const [user_state, setUserState] = useState<any>(data?.user || null);
  const [updateUser] = useMutation(UPDATE_USER);
  const [isLoading, setIsLoading] = useState(false);
  const [stateInitialized, setStateInitialized] = useState(false);

  // Register service worker message listener for state updates
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'STATE_CACHED') {
        console.log('State cached by service worker', event.data.timestamp);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  //get user every 5 minutes and update the user state
  useEffect(() => {
    const handler = localStorage.getItem("handlerToken") || "";
    if (user_state?.id === undefined || !stateInitialized) return;
    if (handler !== "") {
      localStorage.removeItem("userToken");
      return;
    }
    const interval = setInterval(() => {
      if (user_state?.id === undefined) return;
      getUser({
        variables: {
          userId: Number(user_state?.id),
        },
        onCompleted(data) {
          if (data?.user !== null) {
            setUserState(data?.user);
            // Save to both localStorage and IndexedDB
            saveUserState(data?.user).catch(err => console.error('Error saving user state:', err));
          }
        },
        onError(error) {
          console.log({ error });
        },
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [getUser, user_state, stateInitialized]);
  useEffect(() => {
    const initializeUserState = async () => {
      setIsLoading(true);

      // Check for handler token first
      const handlerState = localStorage.getItem("handlerToken");
      if (handlerState) {
        setIsLoading(false);
        setStateInitialized(true);
        return;
      }

      // Try to get the user state from IndexedDB
      try {
        // Look for an existing user ID in localStorage as a starting point
        const localState = localStorage.getItem("userToken");
        let userIdToFetch: number | null = null;

        if (localState && localState !== "undefined") {
          try {
            const parsedUser = JSON.parse(localState);
            userIdToFetch = parsedUser?.id || null;
          } catch (parseError) {
            console.error('Error parsing local user token:', parseError);
          }
        }

        if (userIdToFetch) {
          // Try to get the user from IndexedDB
          const storedUser = await getUserState(userIdToFetch);
          
          if (storedUser) {
            setUserState(storedUser);
            setIsLoading(false);
            setStateInitialized(true);
            
            // Update page routing if needed
            if (window.location.pathname === "/") {
              window.location.replace("/dashboard/friend-request");
            }
            
            return;
          }
        }

        // If we reach here, no valid user state was found
        if (
          window.location.pathname.includes("login") ||
          window.location.pathname.includes("choose-a-plan") ||
          window.location.pathname.includes("get-started") ||
          window.location.pathname.includes("admin") ||
          window.location.pathname.includes("forget-password") ||
          window.location.pathname.includes("reset-password") ||
          window.location.pathname.includes("pre-verify") ||
          window.location.pathname.includes("verification") ||
          window.location.pathname.includes("terms-and-condition") ||
          window.location.pathname.includes("super") ||
          window.location.pathname === "/"
        ) {
          setIsLoading(false);
          setStateInitialized(true);
          return;
        }

        // If we're not on a public route and have no user, redirect to login
        window.location.replace("/login");
      } catch (error) {
        console.error('Error during user state initialization:', error);
        
        // Fallback to localStorage
        const localState = localStorage.getItem("userToken");
        if (localState && localState !== "undefined") {
          try {
            const parsedUser = JSON.parse(localState);
            setUserState(parsedUser);
            
            // Update page routing if needed
            if (window.location.pathname === "/") {
              window.location.replace("/dashboard/friend-request");
            }
          } catch (parseError) {
            console.error('Error parsing fallback local user token:', parseError);
            
            // Last resort: redirect to login if not on a public page
            if (
              !window.location.pathname.includes("login") &&
              !window.location.pathname.includes("choose-a-plan") &&
              !window.location.pathname.includes("get-started") &&
              !window.location.pathname.includes("admin") &&
              !window.location.pathname.includes("forget-password") &&
              !window.location.pathname.includes("reset-password") &&
              !window.location.pathname.includes("pre-verify") &&
              !window.location.pathname.includes("verification") &&
              !window.location.pathname.includes("terms-and-condition") &&
              !window.location.pathname.includes("super") &&
              window.location.pathname !== "/"
            ) {
              window.location.replace("/login");
            }
          }
        }
      }
      
      setIsLoading(false);
      setStateInitialized(true);
      localStorage.removeItem("handlerToken");
    };

    initializeUserState();
  }, []);

  const saveUser = (payload, callback?: any, errorCB?: any) => {
    const updatedUser = { ...user_state, ...payload };
    const json_user_data = JSON.stringify(updatedUser);
    
    updateUser({
      variables: {
        updateUserId: Number(user_state.id),
        payload,
      },
      onCompleted: (data) => {
        if (data?.updateUser) {
          setUserState(updatedUser);
          
          // Save to both localStorage and IndexedDB for persistence
          localStorage.setItem("userToken", json_user_data);
          saveUserState(updatedUser).catch(err => console.error('Error saving updated user state:', err));
          
          localStorage.removeItem("handlerToken");
          
          if (typeof callback === "function") {
            return callback();
          }
          return;
        }
      },
      onError: (error) => {
        console.log(error);
        return errorCB?.();
      },
    });
  };

  const setUser = (payload) => {
    const updatedUser = { ...user_state, ...payload };
    const json_user_data = JSON.stringify(updatedUser);
    
    // Clear previous data
    localStorage.clear();
    
    // Set new user data in both localStorage and IndexedDB
    localStorage.setItem("userToken", json_user_data);
    saveUserState(updatedUser).catch(err => console.error('Error in setUser:', err));
    
    setUserState(updatedUser);
  };
  const logoutUser = () => {
    try {
      // Clear all storage - both localStorage and IndexedDB
      clearAllState().catch(err => console.error('Error clearing state during logout:', err));
      setUserState(null);
    } catch (error) {
      console.log(error);
      
      // Fallback to just clearing localStorage
      localStorage.removeItem("userToken");
      localStorage.removeItem("handlerToken");
      localStorage.clear();
      setUserState(null);
    }
  };

  const userRefetch = () => {
    refetch();
    if (data?.user) {
      // Update both localStorage and IndexedDB
      localStorage.removeItem("userToken");
      localStorage.setItem("userToken", JSON.stringify(data?.user));
      saveUserState(data?.user).catch(err => console.error('Error saving refetched user:', err));
      setUserState(data?.user);
    }
  };

  const values = useMemo(
    () => ({
      user: user_state,
      saveUser,
      logoutUser,
      setUser,
      userRefetch,
    }),
    [user_state]
  );

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};
